import type { ChangeEventHandler } from 'react';
import { useRef, useState } from 'react';

import useConcessionConsultationPrice from '../concession/useConcessionConsultationPrice';
import usePostPaymentsChargeForConsult from '../rest/usePostPaymentsChargeForConsult';

import { getConsultationType } from '@/components/PatientPageV2/utils/getConsultationType';
import type { ConsultationPricingTier } from '@/types/consultation.types';
import { Logger } from '@/utils/logger';
import { datadogRum } from '@datadog/browser-rum';
import { PaymentRequestType } from '@montugroup/pms-api-contracts/build/api-pms/consultation/contracts/request-payment.contract';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import type { ConsultPaymentMethod } from './useConsult';
import useConsultationPrice from './useConsultationPrice';
import { useCreateConsultationPaymentRequest } from './useCreateConsultationPaymentRequest';

const logger = new Logger('PMS:useChargeConsult');

const consultDiscountMin = 0;
const consultDiscountMax = 99;

// Don't allow a practitioner to submit a negative discount or a discount over 100%
function sanitizeDiscountRate(consultDiscountRate: string) {
  const parsed = parseFloat(consultDiscountRate) || 0;
  if (parsed > consultDiscountMin && parsed <= consultDiscountMax) {
    return String(parsed);
  }
  return '0';
}

/**
 * Maps a consultation pricing tier to its corresponding payment request type.
 * If no pricing tier is provided, returns STANDARD as the default payment request type.
 *
 * @param pricingTier - The consultation pricing tier to map
 * @returns The corresponding payment request type:
 *   - STANDARD for 'standard' tier or undefined input
 *   - COMPLIMENTARY for 'complimentary' tier
 *   - CONCESSION for 'concession' tier
 */
export function getPaymentRequestType(pricingTier: ConsultationPricingTier | undefined) {
  if (!pricingTier) {
    return PaymentRequestType.STANDARD;
  }
  switch (pricingTier.name.toLowerCase()) {
    case 'standard':
      return PaymentRequestType.STANDARD;
    case 'complimentary':
      return PaymentRequestType.COMPLIMENTARY;
    case 'concession':
      return PaymentRequestType.CONCESSION;
    default:
      return PaymentRequestType.STANDARD;
  }
}

// Mark everything as optional for now since this hook is consumed in a js page that optionally displays a specific
// consult.
export type UseChargeConsultProps = Partial<{
  consultationId: number;
  consultationType: 'Initial' | 'Follow-up';
  consultationPaymentMethod: ConsultPaymentMethod;
  doctorId: string | number;
  patientEmail: string | null;
  isConcessionPatient?: boolean;
  isComplimentary: boolean;
}>;

// We allow clinicians to charge for consultations in a few different ways, hence consolidate the business logic
// here. They may choose to charge the original rate defined in harness feature flag, charge a concession rate or even
// provide a manually set discount rate. Additionally, depending on the type and state of a consult, different
// clinicians may be able to perform different actions.
export function useChargeConsult({
  consultationId,
  consultationType,
  consultationPaymentMethod,
  doctorId,
  patientEmail,
  isConcessionPatient,
  isComplimentary,
}: UseChargeConsultProps) {
  const {
    consultationPrice: initialConsultationPrice,
    consultationChargePrice: initialConsultationChargePrice,
    formattedConsultationPrice: formattedInitialConsultationPrice,
    followUpConsultationPrice: followUpConsultationPrice,
    followUpConsultationChargePrice: followUpConsultationChargePrice,
    formattedFollowUpConsultationPrice: formattedFollowUpConsultationPrice,
  } = useConsultationPrice();

  const {
    consultationPrice: concessionInitialConsultationPrice,
    formattedConsultationPrice: formattedConcessionInitialConsulationPrice,
    followUpConsultationPrice: concessionFollowUpConsultationPrice,
    formattedFollowUpConsultationPrice: formattedConcessionFollowUpConsultationPrice,
    discountRate: concessionConsultationDiscountRate,
  } = useConcessionConsultationPrice();

  // Allow clinicians to manually provide whatever discount percent they want
  const [consultDiscountRate, setConsultDiscountRate] = useState('0');

  const ldClient = useLDClient();

  const consultAndClinicianType = getConsultationType({
    consult_type: consultationType,
    doctor_id: doctorId,
  });
  const isFollowUpConsult = consultationType === 'Follow-up';

  // Derive current consultation type prices
  const [
    consultationPrice,
    consultationChargePrice,
    formattedConsultationPrice,
    concessionConsultationPrice,
    formattedConcessionConsulationPrice,
  ] = isFollowUpConsult
    ? [
        followUpConsultationPrice,
        followUpConsultationChargePrice,
        formattedFollowUpConsultationPrice,
        concessionFollowUpConsultationPrice,
        formattedConcessionFollowUpConsultationPrice,
      ]
    : [
        initialConsultationPrice,
        initialConsultationChargePrice,
        formattedInitialConsultationPrice,
        concessionInitialConsultationPrice,
        formattedConcessionInitialConsulationPrice,
      ];

  const discountInputProps = { min: consultDiscountMin, max: consultDiscountMax };

  // No need to be reactive as this is dependent on a click handler and axios loading state
  const lastHandle = useRef<null | 'consult' | 'concession'>(null);
  const { doPost: doPostChargeForConsult, loading: loadingCharge } = usePostPaymentsChargeForConsult();

  const isLoadingConsult = loadingCharge && lastHandle.current === 'consult';
  const isLoadingConcession = loadingCharge && lastHandle.current === 'concession';

  const handleChangeConsultDiscountRate: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    const newValue = e.target.value;
    const sanitizedValue = sanitizeDiscountRate(newValue);

    logger.info('handleChangeConsultDiscountRate', { newValue, sanitizedValue });

    // Prevents values outside the sanitizeDiscountRate range from being inputable in the discount % input
    if (!newValue || parseInt(newValue) === parseInt(sanitizedValue)) {
      setConsultDiscountRate(sanitizedValue);
    }
  };

  const doChargeConsult = async () => {
    if (!consultationId || !patientEmail) {
      logger.error('doChargeConsult', { consultationId, hasPatientEmail: !!patientEmail });
      return { data: null, error: new Error('Cannot charge consult with invalid props') };
    }

    lastHandle.current = 'consult';
    const discountRate = isComplimentary ? '100' : consultDiscountRate;
    const discountType = isComplimentary ? 'COMPLIMENTARY' : 'NONE';

    const result = await doPostChargeForConsult({
      consultationId,
      emailId: patientEmail,
      amount: consultationChargePrice,
      discountRate,
      ...(isComplimentary && { discountType: 'COMPLIMENTARY' }),
      paymentMethod: consultationPaymentMethod,
    });
    logger.info('doChargeConsult', {
      consultationId,
      amount: consultationChargePrice,
      discountRate,
      ...(isComplimentary && { discountType: 'COMPLIMENTARY' }),
      paymentMethod: consultationPaymentMethod,
      ...result.data,
    });
    ldClient?.track(
      'consultation-amount',
      {
        discountRate,
        paymentMethod: consultationPaymentMethod,
        discountType,
        consultAndClinicianType,
      },
      consultationChargePrice,
    );
    datadogRum.addAction('consultation-amount', {
      discountRate,
      paymentMethod: consultationPaymentMethod,
      discountType,
      amount: consultationChargePrice,
      consultAndClinicianType,
    });
    return result;
  };

  const doChargeConcession = async () => {
    if (!consultationId || !patientEmail) {
      logger.error('doChargeConcession', { consultationId, hasPatientEmail: !!patientEmail });
      return { data: null, error: new Error('Cannot charge concession consult with invalid props') };
    }

    lastHandle.current = 'concession';
    const discountRate = String(isComplimentary ? '100' : concessionConsultationDiscountRate);
    const discountType = isComplimentary ? 'COMPLIMENTARY' : 'CONCESSION';

    ldClient?.track(
      'consultation-amount',
      {
        discountRate,
        paymentMethod: consultationPaymentMethod,
        discountType,
        consultAndClinicianType,
      },
      consultationChargePrice,
    );
    datadogRum.addAction('consultation-amount', {
      discountRate,
      paymentMethod: consultationPaymentMethod,
      discountType,
      amount: consultationChargePrice,
      consultAndClinicianType,
    });
    const result = await doPostChargeForConsult({
      consultationId,
      emailId: patientEmail,
      amount: consultationChargePrice,
      discountRate,
      discountType,
      paymentMethod: consultationPaymentMethod,
    });
    logger.info('doChargeConcession', {
      consultationId,
      amount: consultationChargePrice,
      discountRate,
      discountType,
      paymentMethod: consultationPaymentMethod,
      ...result.data,
    });

    return result;
  };

  const {
    createPaymentRequest,
    isPending: isShopifyPaymentRequestPending,
    error: shopifyPaymentRequestError,
    isSuccess: isShopifyPaymentRequestSuccess,
  } = useCreateConsultationPaymentRequest(consultationId);

  const createShopifyPaymentRequest = async (
    pricingTier: ConsultationPricingTier,
    paymentRequestType?: PaymentRequestType,
  ) => {
    if (paymentRequestType) {
      await createPaymentRequest(paymentRequestType);
      return;
    }
    // no pricing tier means a payment request has not been sent before
    if (!pricingTier) {
      if (isComplimentary) {
        await createPaymentRequest(PaymentRequestType.COMPLIMENTARY);
      } else if (isConcessionPatient) {
        await createPaymentRequest(PaymentRequestType.CONCESSION);
      } else {
        await createPaymentRequest(PaymentRequestType.STANDARD);
      }
    } else {
      await createPaymentRequest(getPaymentRequestType(pricingTier));
    }
  };

  return {
    consultationPrice,
    consultationChargePrice,
    formattedConsultationPrice,
    concessionConsultationPrice,
    formattedConcessionConsulationPrice,
    consultDiscountRate,
    handleChangeConsultDiscountRate,
    concessionConsultationDiscountRate,
    discountInputProps,
    loadingChargeConsult: isLoadingConsult,
    loadingChargeConcession: isLoadingConcession,
    doChargeConsult,
    doChargeConcession,
    consultAndClinicianType,
    isComplimentary,
    createShopifyPaymentRequest,
    isShopifyPaymentRequestPending,
    shopifyPaymentRequestError,
    isShopifyPaymentRequestSuccess,
  };
}

export default useChargeConsult;
